import { BRAND_COLORS } from "@/Utils/brandColors";
import { useEffect, useState, useRef, useCallback } from "react";
import { getAvatarSharingData } from "@/Utils/avatarSharingStorage";
import "./styles.scss";

interface RedirectProgressProps {
  durationSeconds?: number;
  // "default" keeps the existing card (title + thick bar + fallback button),
  // used by SendMeasurements. "minimal" is the SubmitScan redesign: a slim
  // progress bar + a quiet "Continue now" text link.
  variant?: "default" | "minimal";
  // When true, the redirect URL also carries `ttf-cancelled=true`. The store
  // widget uses this to tell a backed-out share from a completed one: on a
  // cancel it must NOT auto-open / show the "scan shared" UI (see the widget's
  // useAutoOpenWidget). Success redirects leave this false.
  cancelled?: boolean;
}

const RedirectProgress = ({
  durationSeconds = 5,
  variant = "default",
  cancelled = false,
}: RedirectProgressProps) => {
  const [progress, setProgress] = useState(0);
  const { redirectUrl: localRedirectUrl } = getAvatarSharingData();

  // Helper function to add PWA parameter to URL
  const addPWAParameter = (url: string | null): string => {
    if (!url) return "";

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set("ttf-from-pwa", "true");
      if (cancelled) {
        urlObj.searchParams.set("ttf-cancelled", "true");
      }
      return urlObj.toString();
    } catch (error) {
      console.error("Error adding PWA parameter to URL:", error);
      return url; // Return original URL if parsing fails
    }
  };

  // save the redirectUrl in useRef to avoid re-rendering or losing them after data is cleared
  const redirectUrl = useRef<string>("");
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (localRedirectUrl) {
      redirectUrl.current = localRedirectUrl;
    }
  }, [localRedirectUrl]);

  const performRedirect = useCallback(() => {
    if (!redirectUrl.current || hasRedirected.current) return;
    hasRedirected.current = true;

    const modifiedUrl = addPWAParameter(redirectUrl.current);
    // Try multiple approaches for maximum iOS compatibility
    try {
      // Method 1: window.open with _self (works in most iOS contexts)
      const opened = window.open(modifiedUrl, "_self");

      // Method 2: If window.open fails, try location.assign
      if (!opened) {
        window.location.assign(modifiedUrl);
      }
    } catch (e) {
      // Method 3: Last resort - try all location methods
      try {
        window.top!.location.href = modifiedUrl;
      } catch (topError) {
        try {
          window.location.href = modifiedUrl;
        } catch (hrefError) {
          window.location.replace(modifiedUrl);
        }
      }
    }
  }, []);

  // Animate progress bar over durationSeconds
  useEffect(() => {
    const intervalMs = 50;
    const totalSteps = (durationSeconds * 1000) / intervalMs;
    const increment = 100 / totalSteps;
    let currentProgress = 0;

    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        performRedirect();
      }
      setProgress(currentProgress);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [durationSeconds, performRedirect]);

  const handleManualRedirect = () => {
    if (redirectUrl.current) {
      hasRedirected.current = false;
      performRedirect();
    }
  };

  // Don't render if no redirectUrl
  if (!redirectUrl.current) {
    console.error("No redirect URL found");
    return (
      <div
        className={`redirect-progress${
          variant === "minimal" ? " redirect-progress--minimal" : ""
        }`}
      />
    );
  }

  if (variant === "minimal") {
    return (
      <div className="redirect-progress redirect-progress--minimal">
        <div className="redirect-progress__bar-track redirect-progress__bar-track--minimal">
          <div
            className="redirect-progress__bar-fill redirect-progress__bar-fill--minimal"
            style={{ animationDuration: `${durationSeconds}s` }}
          />
        </div>

        <button
          type="button"
          onClick={handleManualRedirect}
          className="redirect-progress__continue"
        >
          Continue now
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path
              d="M5 3 L10 7.5 L5 12"
              stroke={BRAND_COLORS.primary0670}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="redirect-progress">
      <h2 className="redirect-progress__title">
        Redirecting to your fit prediction...
      </h2>

      <div className="redirect-progress__bar-track">
        <div
          className="redirect-progress__bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="redirect-progress__fallback">
        <p className="redirect-progress__fallback-text">
          Taking too long? Click below:
        </p>
        <button
          onClick={handleManualRedirect}
          className="redirect-progress__button primary-button"
        >
          Redirect
        </button>
      </div>
    </div>
  );
};

export default RedirectProgress;
